import Phaser from 'phaser';

/**
 * Sprite assets are generated at 512x or 1024x by the AI model.
 * The game world is built around 48-pixel tiles.
 * This module hides the scale + physics-body bookkeeping so each entity can
 * declare its desired displayed size and we get a body that matches it.
 */

function nativeSize(sprite: Phaser.GameObjects.Sprite): { w: number; h: number } {
  const src = sprite.texture.getSourceImage() as HTMLImageElement | { width: number; height: number };
  return { w: src.width || sprite.width || 1, h: src.height || sprite.height || 1 };
}

export function scaleSpriteToTile(sprite: Phaser.GameObjects.Sprite, targetPx: number): number {
  const { w, h } = nativeSize(sprite);
  const longest = Math.max(w, h);
  const s = targetPx / longest;
  sprite.setScale(s);
  return s;
}

/**
 * Resize the sprite's dynamic body to `bodyPx × bodyPx` WORLD pixels (after scale),
 * centered on the displayed sprite. Body.setSize takes native texture pixels in
 * Phaser 3, so we divide by the current scale to convert.
 */
export function fitBodyToTile(sprite: Phaser.Physics.Arcade.Sprite, bodyPx: number) {
  const body = sprite.body as Phaser.Physics.Arcade.Body;
  if (!body) return;
  const scale = sprite.scaleX || 1;
  const bodyNative = bodyPx / scale;
  const { w, h } = nativeSize(sprite);
  body.setSize(bodyNative, bodyNative);
  body.setOffset((w - bodyNative) / 2, (h - bodyNative) / 2);
}

/**
 * For static bodies (Block, Bomb, Explosion). Sets displayed size and rebuilds
 * the static body to match. `bodyPx` defaults to `displayPx`.
 */
export function fitStaticBodyToTile(
  sprite: Phaser.Physics.Arcade.Sprite,
  displayPx: number,
  bodyPx = displayPx,
) {
  const { w, h } = nativeSize(sprite);
  const longest = Math.max(w, h);
  const s = displayPx / longest;
  sprite.setScale(s);
  const body = sprite.body as Phaser.Physics.Arcade.StaticBody | null;
  if (!body) return;
  const bodyNative = bodyPx / s;
  body.setSize(bodyNative, bodyNative);
  body.setOffset((w - bodyNative) / 2, (h - bodyNative) / 2);
  sprite.refreshBody();
}
